import React, { useState, useEffect } from "react";
import Sidebar from "../../components/sidebar/Sidebar";
import SectionHeader from "../../components/sectionHeader/SectionHeader";
import MainSection from "../../components/mainLayout/Main";
import { addEmployee, getAllServices } from "../../redux/actions/authActions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

function AddEmployee() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phoneNumber: "",
		department: "",
		password: "",
	});
	const [isCustomPassword, setIsCustomPassword] = useState(false);
	const [showPassword, setShowPassword] = useState(true);

	useEffect(() => {
		async function func() {
			try {
				const token = localStorage.getItem("token");
				if (token) await dispatch(getAllServices(token));
			} catch (err) {
				console.error(err);
			}
		}
		func();
	}, [dispatch]);

	const computeDefaultPassword = (nameVal, phoneVal) => {
		const cleanName = (nameVal || "").replace(/\s+/g, "");
		const lastDigits = phoneVal && phoneVal.length >= 5 ? phoneVal.slice(-5) : (phoneVal || "");
		return cleanName + lastDigits;
	};

	const handleChange = (e) => {
		const { id, value } = e.target;

		if (id === "phoneNumber") {
			const numericValue = value.replace(/\D/g, "");
			if (numericValue.length <= 10) {
				setFormData((prev) => {
					const updated = { ...prev, [id]: numericValue };
					if (!isCustomPassword) {
						updated.password = computeDefaultPassword(prev.name, numericValue);
					}
					return updated;
				});
			}
		} else if (id === "name") {
			const lettersOnly = value.replace(/[^a-zA-Z\s]/g, "");
			if (value.length < 21) {
				setFormData((prev) => {
					const updated = { ...prev, [id]: lettersOnly };
					if (!isCustomPassword) {
						updated.password = computeDefaultPassword(lettersOnly, prev.phoneNumber);
					}
					return updated;
				});
			}
		} else if (id === "password") {
			setIsCustomPassword(true);
			setFormData((prev) => ({ ...prev, [id]: value }));
		} else {
			setFormData((prev) => ({ ...prev, [id]: value }));
		}
	};

	const handleResetPassword = () => {
		setIsCustomPassword(false);
		setFormData((prev) => ({
			...prev,
			password: computeDefaultPassword(prev.name, prev.phoneNumber),
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (formData.name.length > 20)
			return message.error("Employee name too long, should not exceed 20 characters");
		if (formData.phoneNumber.length < 10)
			return message.error("Invalid Phone Number");
		if (!formData.department)
			return message.error("Please select a department");

		try {
			await dispatch(addEmployee(formData));
			navigate("/team-management");
		} catch (error) {
			console.error("Error in handleSubmit:", error);
		}
	};

	return (
		<div className="flex bg-gray-100 min-h-screen">
			<Sidebar />
			<MainSection>
				<div className="container mx-auto px-4 py-8">
					<SectionHeader title="Add Employee" />
					<div className="bg-white rounded-xl shadow-lg p-8 mt-6">
						<form onSubmit={handleSubmit}>
							<div className="space-y-6">
								{/* Employee Name */}
								<div>
									<label
										htmlFor="name"
										className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
									>
										Employee Name
									</label>
									<input
										type="text"
										id="name"
										value={formData.name}
										onChange={handleChange}
										className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
										placeholder="Enter employee name"
										required
									/>
								</div>

								{/* Email Address */}
								<div>
									<label
										htmlFor="email"
										className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
									>
										Email Address
									</label>
									<input
										type="email"
										id="email"
										value={formData.email}
										onChange={handleChange}
										className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
										placeholder="Enter email address"
										required
									/>
								</div>

								{/* Phone Number */}
								<div>
									<label
										htmlFor="phoneNumber"
										className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
									>
										Phone Number
									</label>
									<input
										type="tel"
										id="phoneNumber"
										value={formData.phoneNumber}
										onChange={handleChange}
										className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
										placeholder="Enter phone number"
										required
									/>
								</div>

								{/* Department Dropdown */}
								<div>
									<label
										htmlFor="department"
										className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
									>
										Department
									</label>
									<select
										id="department"
										value={formData.department}
										onChange={handleChange}
										className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
										required
									>
										<option disabled value="">
											Select department
										</option>
										<option value="HR">HR</option>
										<option value="Engineering">Engineering</option>
										<option value="Marketing">Marketing</option>
										<option value="Sales">Sales</option>
									</select>
								</div>

								{/* Password */}
								<div>
									<div className="flex justify-between items-center mb-2">
										<label
											htmlFor="password"
											className="text-xs font-semibold text-gray-500 uppercase tracking-wide"
										>
											Password
										</label>
										<div className="flex items-center space-x-2">
											{isCustomPassword && (
												<button
													type="button"
													onClick={handleResetPassword}
													className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
												>
													Reset to Default
												</button>
											)}
											<span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-medium">
												{isCustomPassword ? "Custom" : "Default"}
											</span>
										</div>
									</div>
									<div className="relative">
										<input
											type={showPassword ? "text" : "password"}
											id="password"
											value={formData.password}
											onChange={handleChange}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10 transition-colors font-mono"
											placeholder="Employee password"
											required
										/>
										<button
											type="button"
											onClick={() => setShowPassword(!showPassword)}
											className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
											title={showPassword ? "Hide password" : "Show password"}
										>
											{showPassword ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
										</button>
									</div>
									<p className="mt-1 text-[11px] text-gray-500">
										Default: Name (no spaces) + last 5 digits of phone number. Admin can edit this manually.
									</p>
								</div>

								{/* Submit Button */}
								<div className="pt-4">
									<button
										type="submit"
										className="w-full flex justify-center py-2 px-4 rounded-full shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
									>
										Create Employee
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</MainSection>
		</div>
	);
}

export default AddEmployee;
